<template>
  <modal id="gatewayForm"
         v-model="visible"
         :title="$t('g-gatewayForm:update', { gateway: gatewayToEdit.eui })"
         :callback="update"
         size="lg"
  >
    <small slot="modal-header-right" class="text-muted font-italic">{{ $t('g-gatewayForm:required') | capitalize }}</small>

    <div slot="modal-body">
      <div class="form-row">
        <div class="form-group col-lg-6">
          <label for="fleet" class="control-label">{{ $t('g-gatewayForm:fleet') | capitalize }} *</label>
          <select id="fleet" v-model="newFleet" class="form-control">
            <template v-if="isRole.superAdmin">
              <optgroup v-for="(fleets, customer) in groupedFleets" :key="customer" :label="customer">
                <option v-for="fleet in fleets" :key="fleet.id" :value="fleet">{{ fleet.name }}</option>
              </optgroup>
            </template>

            <template v-else>
              <option v-for="fleet in $store.getters.allFleets" :key="fleet.id" :value="fleet">{{ fleet.name }}</option>
            </template>
          </select>
        </div>

        <div class="form-group col-lg-6">
          <label for="name" class="control-label">{{ $t('g-gatewayForm:name') | capitalize }}</label>
          <input id="name"
                 v-model="name"
                 v-validate="'text'"
                 :data-vv-as="$t('g-gatewayForm:name')"
                 :class="{ 'is-invalid': errors.has('name') }"
                 type="text"
                 name="name"
                 class="form-control"
          >
          <div class="invalid-feedback">{{ errors.first('name') }}</div>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group" :class="countries.length > 0 ? 'col-lg-3' : 'col-lg-6'">
          <label for="region" class="control-label">{{ $t('g-gatewayForm:region') | capitalize }} *</label>
          <select id="region"
                  v-model="region"
                  v-validate="'required'"
                  :data-vv-as="$t('g-gatewayForm:region')"
                  :class="{ 'is-invalid': errors.has('region') }"
                  name="region"
                  class="form-control"
          >
            <option v-for="r in regions" :key="r" :value="r">{{ r }}</option>
          </select>
          <div class="invalid-feedback">{{ errors.first('region') }}</div>
        </div>

        <div v-show="countries.length > 0" class="form-group col-lg-3">
          <label for="country" class="control-label">{{ $t('g-gatewayForm:country') | capitalize }}</label>
          <select id="country"
                  v-model="country"
                  name="country"
                  class="form-control"
          >
            <option />
            <option v-for="c in countries" :key="c" :value="c">{{ $t('countries:' + c) }}</option>
          </select>
        </div>

        <div class="form-group col-lg-6">
          <label for="visibility" class="control-label d-block">{{ $t('g-gatewayForm:visibility') | capitalize }}</label>
          <div class="pt-2">
            <input id="visibilityPrivate"
                   v-model="visibility"
                   v-validate="'required'"
                   :data-vv-as="$t('g-gatewayForm:visibility')"
                   :class="{ 'is-invalid': errors.has('visibility') }"
                   type="radio"
                   value="PRIVATE"
                   name="visibility"
                   class="form-control"
            >
            <label for="visibilityPrivate" class="control-label">{{ $t('g-gatewayForm:visibilityPrivate') | capitalize }}</label>

            <input id="visibilityPublic"
                   v-model="visibility"
                   :class="{ 'is-invalid': errors.has('visibility') }"
                   type="radio"
                   value="PUBLIC"
                   name="visibility"
                   class="form-control"
            >
            <label for="visibilityPublic" class="control-label">{{ $t('g-gatewayForm:visibilityPublic') | capitalize }}</label>

            <div class="invalid-feedback">{{ errors.first('visibility') }}</div>
          </div>
        </div>
      </div>

      <div class="form-row">
        <div v-if="$store.getters.gatewayFeature(gatewayToEdit, 'gps')" class="form-group col-lg-4">
          <label for="gpsPositionAllowed" class="control-label d-block">{{ $t('g-gatewayForm:geolocation') | capitalize }}</label>
          <input id="gpsPositionAllowed"
                 v-model="gpsPositionAllowed"
                 type="checkbox"
          >
          <label for="gpsPositionAllowed" />
          <label for="gpsPositionAllowed" class="control-label">{{ $t('g-gatewayForm:gpsPositionAllowed') | capitalize }}</label>
        </div>

        <div class="form-group col-lg-4">
          <label for="dlAllowed" class="control-label d-block">{{ $t('g-gatewayForm:downlink') | capitalize }}</label>
          <input id="dlAllowed"
                 v-model="dlAllowed"
                 type="checkbox"
          >
          <label for="dlAllowed" />
          <label for="dlAllowed" class="control-label">{{ $t('g-gatewayForm:dlAllowed') | capitalize }}</label>
        </div>

        <div class="form-group col-lg-4">
          <label for="adaptiveLatencyEnabled" class="control-label d-block">{{ $t('g-gatewayForm:adaptiveLatency') | capitalize }}</label>
          <input id="adaptiveLatencyEnabled"
                 v-model="adaptiveLatencyEnabled"
                 type="checkbox"
          >
          <label for="adaptiveLatencyEnabled" />
          <label for="adaptiveLatencyEnabled" class="control-label">{{ $t('g-gatewayForm:adaptiveLatencyEnabled') | capitalize }}</label>
        </div>
      </div>

      <h3 class="mt-3">{{ $t('g-gatewayForm:ipPortTitle') | capitalize }}</h3>

      <div class="form-row">
        <div class="form-group col-lg-8">
          <label for="ip" class="control-label">{{ $t('g-gatewayForm:ip') | capitalize }}<span v-if="ip!=='' || port!==''"> *</span></label>
          <input id="ip"
                 v-model="ip"
                 v-validate="{rules: {required: port!=='', ip: true}}"
                 :class="{ 'is-invalid': errors.has('ip') }"
                 :data-vv-as="$t('g-gatewayForm:ip')"
                 type="text"
                 name="ip"
                 class="form-control"
          >
          <div class="invalid-feedback">{{ errors.first('ip') }}</div>
        </div>

        <div class="form-group col-lg-4">
          <label for="port" class="control-label">{{ $t('g-gatewayForm:port') | capitalize }}<span v-if="ip!=='' || port!==''"> *</span></label>
          <input id="port"
                 v-model="port"
                 v-validate="{rules: {required: ip!=='', number:true, between: [1,65535]}}"
                 :class="{ 'is-invalid': errors.has('port') }"
                 :data-vv-as="$t('g-gatewayForm:port')"
                 type="text"
                 name="port"
                 class="form-control"
          >
          <div class="invalid-feedback">{{ errors.first('port') }}</div>
        </div>
      </div>

      <h3 class="mt-3">{{ $t('g-gatewayForm:overrideCoordinates') | capitalize }}</h3>

      <div class="form-row">
        <div class="form-group col-lg-4">
          <label for="latitude" class="control-label">{{ $t('g-gatewayForm:latitude') | capitalize }}</label>
          <input id="latitude"
                 v-model="latitude"
                 v-validate="'number|between:-90,90'"
                 :class="{ 'is-invalid': errors.has('latitude') }"
                 :data-vv-as="$t('g-gatewayForm:latitude')"
                 type="text"
                 name="latitude"
                 class="form-control"
          >
          <small class="form-text text-muted">{{ $t('g-gatewayForm:latitudeUnit') | capitalize }}</small>
          <div class="invalid-feedback">{{ errors.first('latitude') }}</div>
        </div>

        <div class="form-group col-lg-4">
          <label for="longitude" class="control-label">{{ $t('g-gatewayForm:longitude') | capitalize }}</label>
          <input id="longitude"
                 v-model="longitude"
                 v-validate="'number|between:-180,180'"
                 :class="{ 'is-invalid': errors.has('longitude') }"
                 :data-vv-as="$t('g-gatewayForm:longitude')"
                 type="text"
                 name="longitude"
                 class="form-control"
          >
          <small class="form-text text-muted">{{ $t('g-gatewayForm:longitudeUnit') | capitalize }}</small>
          <div class="invalid-feedback">{{ errors.first('longitude') }}</div>
        </div>

        <div class="form-group col-lg-4">
          <label for="altitude" class="control-label">{{ $t('g-gatewayForm:altitude') | capitalize }}</label>
          <input id="altitude"
                 v-model="altitude"
                 v-validate="'number|decimal:0'"
                 :class="{ 'is-invalid': errors.has('altitude') }"
                 :data-vv-as="$t('g-gatewayForm:altitude')"
                 type="text"
                 name="altitude"
                 class="form-control"
          >
          <small class="form-text text-muted">{{ $t('g-gatewayForm:altitudeUnit') | capitalize }}</small>
          <div class="invalid-feedback">{{ errors.first('altitude') }}</div>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import { groupBy, orderBy, isEmpty, pick, find } from 'lodash-es'
import { regions, countries } from '@/configuration/regions'
import to from '@/modules/await-to-js'
import eventBus from '@/modules/eventBus'

export default {
  model: {
    prop: 'show',
    event: 'change'
  },

  props: {
    // Display or hide modal
    // To be used as v-model
    show: {
      type: Boolean,
      required: true
    },

    gatewayToEdit: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      // Display or hide b-modal
      // It cannot change our props, so we need another variable
      visible: false,
      // Form
      id: '',
      newFleet: {},
      name: '',
      region: '',
      country: '',
      visibility: '',
      ip: '',
      port: '',
      longitude: '',
      latitude: '',
      altitude: '',
      gpsPositionAllowed: false,
      dlAllowed: true,
      adaptiveLatencyEnabled: false
    }
  },

  computed: {
    regions () { return regions },
    countries () { return countries[this.region] || [] },

    // Group fleets by customer for superAdmin
    groupedFleets () {
      // Order by customer name
      // Group by customer name to add customer in lists
      return groupBy(orderBy(this.$store.getters.allFleets, ['customer.name']), 'customer.name')
    }
  },

  watch: {
    // Synchronize show and visible
    // Use change event to update v-model
    show () {
      this.reset()
      this.visible = this.show

      if (this.show) {
        this.$store.dispatch('getAllFleets').then(() => {
          this.newFleet = find(this.$store.getters.allFleets, fleet => fleet.id === this.gatewayToEdit.fleet.id)
        })
      }
    },
    visible () {
      this.$emit('change', this.visible)
    },

    region () {
      if (this.countries.length === 0) this.country = ''
    },

    gatewayToEdit () {
      if (!isEmpty(this.gatewayToEdit)) {
        this.id = this.gatewayToEdit.id
        // newFleet is set after getting all fleets
        this.name = this.gatewayToEdit.name
        this.region = this.gatewayToEdit.region
        this.country = this.gatewayToEdit.country
        this.visibility = this.gatewayToEdit.visibility
        this.ip = this.gatewayToEdit.ip || ''
        this.port = this.gatewayToEdit.port || ''
        this.longitude = this.gatewayToEdit.longitude
        this.latitude = this.gatewayToEdit.latitude
        this.altitude = this.gatewayToEdit.altitude
        this.gpsPositionAllowed = this.gatewayToEdit.gpsPositionAllowed
        this.dlAllowed = this.gatewayToEdit.dlAllowed
        this.adaptiveLatencyEnabled = this.gatewayToEdit.adaptiveLatencyEnabled || false
      }
    }
  },

  methods: {
    async reset () {
      this.id = ''
      this.newFleet = {}
      this.name = ''
      this.region = ''
      this.country = ''
      this.visibility = ''
      this.ip = ''
      this.port = ''
      this.longitude = ''
      this.latitude = ''
      this.altitude = ''
      this.gpsPositionAllowed = false
      this.dlAllowed = true
      this.adaptiveLatencyEnabled = false

      // Reset form validation
      await this.$nextTick()
      this.$validator.reset()
    },

    async update () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      const gateway = pick(this, ['name', 'ip', 'port', 'longitude', 'latitude', 'altitude', 'region', 'country', 'visibility', 'gpsPositionAllowed', 'dlAllowed', 'adaptiveLatencyEnabled'])
      if (this.newFleet.id !== this.gatewayToEdit.fleet.id) gateway.fleet = { id: this.newFleet.id }

      let error
      [error] = await to(this.$api.patchGateway(this.gatewayToEdit, gateway))
      if (error) return

      this.visible = false
      // Success
      eventBus.$emit('message-success', this.$t('g-gatewayForm:editSuccess', { gateway: this.gatewayToEdit.eui }))
      // Reset form
      this.reset()
      // Refresh
      this.$emit('success')
    }
  }
}
</script>
